/**
 * Admin Rubric tab ( item 4).
 *
 * One table of ALL flags -- hardcoded `C*` (toggled via /admin/flags) and
 * learned `LR_*` (toggled via /admin/learned-rules) -- with inline edit for
 * points/severity and an active/enabled toggle. Fixes the operator's
 * "can't enable/disable/adjust rubric" point.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useToastStore } from '../../stores/toast';
import { SEVERITY_LABELS } from '../../config/labels';
import {
  listRubricFlags,
  patchRubricFlag,
  listLearnedRules,
  setLearnedRuleEnabled,
  type RubricFlag,
  type LearnedRule,
} from '../../api/learnedRules';

type Severity = RubricFlag['severity'];

const SEVERITIES: Severity[] = ['AUTO_ESCALATE', 'HIGH', 'MEDIUM', 'LOW'];

const SEVERITY_BADGE: Record<Severity, string> = {
  AUTO_ESCALATE: 'bg-red-100 text-red-700',
  HIGH: 'bg-orange-100 text-orange-700',
  MEDIUM: 'bg-amber-100 text-amber-700',
  LOW: 'bg-slate-100 text-slate-600',
};

interface Row {
  id: string;
  code: string;
  category: number;
  name: string;
  severity: Severity;
  points: number;
  active: boolean;
  source: 'hardcoded' | 'learned';
  versionNo: number | null;
  ruleId?: string;
}

function toRows(flags: RubricFlag[], rules: LearnedRule[]): Row[] {
  const rows: Row[] = flags.map((f) => ({
    id: f.id,
    code: f.flag_code,
    category: f.category,
    name: f.description,
    // Backend returns severity LOWERCASE (auto_escalate/high/medium/low);
    // the badge/label maps are keyed UPPERCASE, so normalize on read so the
    // badge colors render + the severity <select> matches the current value.
    severity: (f.severity as string).toUpperCase() as Severity,
    points: f.points,
    active: f.is_active,
    source: 'hardcoded',
    versionNo: null,
  }));
  for (const r of rules) {
    const av = r.active_version;
    rows.push({
      id: r.id,
      code: r.code,
      category: r.category,
      name: r.name,
      severity: (av?.severity ?? 'LOW').toUpperCase() as Severity,
      points: av?.points ?? 0,
      active: r.enabled,
      source: 'learned',
      versionNo: av?.version_no ?? null,
      ruleId: r.id,
    });
  }
  rows.sort((a, b) => a.code.localeCompare(b.code));
  return rows;
}

export default function RubricTab() {
  const { addToast } = useToastStore();
  const [flags, setFlags] = useState<RubricFlag[]>([]);
  const [rules, setRules] = useState<LearnedRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [editPoints, setEditPoints] = useState<string>('');
  const [editSeverity, setEditSeverity] = useState<Severity>('LOW');
  const [saving, setSaving] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, r] = await Promise.all([
        listRubricFlags(true),
        listLearnedRules(),
      ]);
      setFlags(f);
      setRules(r);
    } catch {
      addToast('Failed to load rubric', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const rows = toRows(flags, rules);

  const startEdit = (row: Row) => {
    setEditing(row.id);
    setEditPoints(String(row.points));
    setEditSeverity(row.severity);
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditPoints('');
  };

  const saveEdit = async (row: Row) => {
    const pts = parseInt(editPoints, 10);
    if (!Number.isFinite(pts) || pts < 0 || pts > 100) {
      addToast('Points must be a non-negative integer', 'error');
      return;
    }
    setSaving(row.id);
    try {
      if (row.source === 'hardcoded') {
        const updated = await patchRubricFlag(row.id, {
          points: pts,
          severity: editSeverity,
        });
        setFlags((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
      } else {
        // Learned rules: severity/points live on the active version. The
        // admin edits a draft + publishes via the Learned Rules sub-section;
        // here we only surface the current values (read-only for learned
        // points/severity) to keep the destructive version flow in one
        // place. Toggle enabled below.
        addToast('Learned-rule points/severity are edited via the Learned Rules sub-section (draft + publish)', 'info');
      }
      addToast('Rubric flag updated', 'success');
      setEditing(null);
    } catch {
      addToast('Failed to update flag', 'error');
    } finally {
      setSaving(null);
    }
  };

  const toggleActive = async (row: Row) => {
    setToggling(row.id);
    const next = !row.active;
    try {
      if (row.source === 'hardcoded') {
        const updated = await patchRubricFlag(row.id, { is_active: next });
        setFlags((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
      } else if (row.ruleId) {
        const updated = await setLearnedRuleEnabled(row.ruleId, next);
        setRules((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      }
      addToast(`${row.code} ${next ? 'enabled' : 'disabled'}`, 'success');
    } catch {
      addToast('Failed to toggle flag', 'error');
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-primary-500" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card p-4 bg-slate-50 border-slate-200">
        <div className="flex items-start gap-2">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18} />
          <p className="text-xs text-slate-600">
            Toggling a flag off here disables it in the next review run (it will
            not fire + will not appear in not-verifiable). Adjusting points/
            severity changes the scored weight. Learned-rule points/severity are
            versioned -- edit them in the Learned Rules sub-section via draft +
            publish.
          </p>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                <th className="px-4 py-3 font-semibold text-slate-600">Code</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Cat</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Name</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Source</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Severity</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Points</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Active</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => {
                const isEditing = editing === row.id;
                const isSaving = saving === row.id;
                const isToggling = toggling === row.id;
                return (
                  <tr key={`${row.source}:${row.id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-slate-700 text-xs">{row.code}</td>
                    <td className="px-4 py-3 text-slate-600">{row.category}</td>
                    <td className="px-4 py-3 text-slate-700 max-w-xs truncate" title={row.name}>{row.name}</td>
                    <td className="px-4 py-3">
                      {row.source === 'learned' ? (
                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                          Learned{row.versionNo != null ? ` v${row.versionNo}` : ''}
                        </span>
                      ) : (
                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-200 text-slate-600">
                          Hardcoded
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isEditing && row.source === 'hardcoded' ? (
                        <select
                          className="text-sm border border-slate-300 rounded px-2 py-1 focus:ring-1 focus:ring-primary-500"
                          value={editSeverity}
                          onChange={(e) => setEditSeverity(e.target.value as Severity)}
                        >
                          {SEVERITIES.map((s) => (
                            <option key={s} value={s}>{SEVERITY_LABELS[s] ?? s}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${SEVERITY_BADGE[row.severity] ?? 'bg-slate-100 text-slate-600'}`}>
                          {SEVERITY_LABELS[row.severity] ?? row.severity}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isEditing && row.source === 'hardcoded' ? (
                        <input
                          type="number"
                          min={0}
                          className="w-16 text-sm border border-slate-300 rounded px-2 py-1 focus:ring-1 focus:ring-primary-500"
                          value={editPoints}
                          onChange={(e) => setEditPoints(e.target.value)}
                        />
                      ) : (
                        <span className="text-xs font-semibold text-slate-600">{row.points}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => toggleActive(row)}
                          disabled={isToggling}
                          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                          style={{ backgroundColor: row.active ? '#16a34a' : '#cbd5e1' }}
                          role="switch"
                          aria-checked={row.active}
                          aria-label={`Toggle ${row.code}`}
                        >
                          {isToggling ? (
                            <Loader2 className="absolute left-1 animate-spin text-white" size={14} />
                          ) : (
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                row.active ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          )}
                        </button>
                        {row.source === 'hardcoded' && !isEditing && (
                          <button
                            onClick={() => startEdit(row)}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Edit
                          </button>
                        )}
                        {isEditing && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => saveEdit(row)}
                              disabled={isSaving}
                              className="text-primary-600 hover:text-primary-700 disabled:opacity-50"
                              aria-label="Save"
                            >
                              {isSaving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="text-slate-400 hover:text-slate-600"
                              aria-label="Cancel"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    No rubric flags configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex items-center gap-1 text-xs text-slate-400">
        <CheckCircle2 size={12} /> {flags.length} hardcoded + {rules.length} learned
      </div>
    </div>
  );
}